import React from 'react';
import { Card } from '../../ui/Card';
import { useWeight } from '../../../context/WeightContext';
import { inchesToCm } from '../../../utils/weight';

export function WeightMetrics() {
  const [measurements, setMeasurements] = React.useState({
    neck: '',
    chest: '',
    waist: '',
    hips: '',
    arms: '',
    thighs: ''
  });
  const [date, setDate] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const { addMeasurements, unitPreferences } = useWeight();

  const handleChange = (key: keyof typeof measurements, value: string) => {
    setMeasurements(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // Convert measurements to cm before storing
    let parsed: any = {
      neck_cm: null,
      chest_cm: null,
      waist_cm: null,
      hips_cm: null,
      upper_arms_cm: null,
      thighs_cm: null,
      recorded_at: new Date(date).toISOString(),
      notes: null
    };

    if (unitPreferences?.preferred_measure_unit === 'in') {
      // Convert inches to cm
      parsed = {
        neck_cm: measurements.neck ? inchesToCm(parseFloat(measurements.neck)) : null,
        chest_cm: measurements.chest ? inchesToCm(parseFloat(measurements.chest)) : null,
        waist_cm: measurements.waist ? inchesToCm(parseFloat(measurements.waist)) : null,
        hips_cm: measurements.hips ? inchesToCm(parseFloat(measurements.hips)) : null,
        upper_arms_cm: measurements.arms ? inchesToCm(parseFloat(measurements.arms)) : null,
        thighs_cm: measurements.thighs ? inchesToCm(parseFloat(measurements.thighs)) : null,
        recorded_at: new Date(date).toISOString(),
        notes: null
      };
    } else {
      // Store as cm
      parsed = {
        neck_cm: measurements.neck ? parseFloat(measurements.neck) : null,
        chest_cm: measurements.chest ? parseFloat(measurements.chest) : null,
        waist_cm: measurements.waist ? parseFloat(measurements.waist) : null,
        hips_cm: measurements.hips ? parseFloat(measurements.hips) : null,
        upper_arms_cm: measurements.arms ? parseFloat(measurements.arms) : null,
        thighs_cm: measurements.thighs ? parseFloat(measurements.thighs) : null,
        recorded_at: new Date(date).toISOString(),
        notes: null
      };
    }

    await addMeasurements(parsed);
    // Clear form after save
    setMeasurements({
      neck: '',
      chest: '',
      waist: '',
      hips: '',
      arms: '',
      thighs: ''
    });
  };

  const fields = [
    { key: 'neck', label: 'Neck' },
    { key: 'chest', label: 'Chest' },
    { key: 'waist', label: 'Waist' },
    { key: 'hips', label: 'Hips' },
    { key: 'arms', label: 'Arms' },
    { key: 'thighs', label: 'Thighs' }
  ];

  // Get unit suffix based on preference
  const getUnitSuffix = () => {
    return unitPreferences?.preferred_measure_unit === 'in' ? 'in' : 'cm';
  };

  const unitSuffix = getUnitSuffix();
  const unitPlaceholder = unitSuffix;

  return (
    <div className="space-y-4 p-4 pb-20">
      <div className="flex items-center justify-between px-2 py-3">
        <h2 className="text-xl font-bold text-on-surface">Body Measurements</h2>
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-sm text-primary">calendar_today</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-transparent border-none text-sm font-medium text-on-surface focus:ring-0 p-0 cursor-pointer"
          />
        </div>
      </div>

      
      <div className="grid grid-cols-2 gap-3 px-2">
        {fields.map((field) => (
          <Card key={field.key} className="bg-surface-container border border-outline">
            <label className="block text-xs font-medium text-on-surface-variant">{field.label} ({unitSuffix})</label>
            <input
              type="number"
              placeholder={unitPlaceholder}
              value={measurements[field.key as keyof typeof measurements]}
              onChange={(e) => handleChange(field.key as keyof typeof measurements, e.target.value)}
              className="mt-1 w-full rounded-lg bg-transparent px-3 py-2 text-lg font-bold text-on-surface focus:outline-none focus:border-primary"
            />
          </Card>
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-surface border-t border-outline">
        <button
          className="w-full rounded-xl bg-primary px-4 py-3.5 font-bold text-primary-contrast shadow-lg shadow-primary/30 active:scale-95 transition-transform"
          onClick={handleSave}
        >
          Save Measurements
        </button>
      </div>
    </div>
  );
}
